/**
 * Admin Notifications Page
 *
 * PRIMARY channel: FCM Web Push (via Firebase, configured by SuperAdmin)
 * SECONDARY channels: SMS / Email / WhatsApp (system-level, for critical msgs only)
 *
 * Flow:
 *  - Admin composes a push notification (title + body)
 *  - Sends to All Customers / All Dealers / Specific user
 *  - FCM delivers as a browser push notification to registered devices
 *  - History table shows all sent notifications + status
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Bell, BellOff, Send, Users, User, Loader2, CheckCircle2,
  XCircle, Clock, RefreshCw, Eye, Smartphone, ChevronDown, Info, Search
} from "lucide-react";
import { toast } from "sonner";
import Select from "react-select";
import { api } from "@/lib/api";
import { useFCM } from "@/hooks/useFCM";
import Pagination from "@/components/Pagination";

const STATUS_BADGE = {
  sent:    { cls: "bg-green-100 text-green-700",  icon: CheckCircle2 },
  partial: { cls: "bg-amber-100 text-amber-700",  icon: Clock },
  failed:  { cls: "bg-red-100 text-red-700",      icon: XCircle },
};

const RECIPIENT_TYPES = [
  { value: "all_customers", label: "All Customers", icon: Users,   desc: "Every active customer" },
  { value: "all_dealers",   label: "All Dealers",   icon: Users,   desc: "Every active dealer" },
  { value: "customer",      label: "One Customer",  icon: User,    desc: "Pick a specific customer" },
  { value: "dealer",        label: "One Dealer",    icon: User,    desc: "Pick a specific dealer" },
];

export default function Notifications() {
  // FCM push permission hook (auto-requests after 3s)
  const { granted: fcmGranted, loading: fcmLoading, request: requestFCM } = useFCM({ autoRequest: false });

  const [fcmConfigured, setFcmConfigured] = useState(null); // null=loading, true, false
  const [registeredCount, setRegisteredCount] = useState(null);

  // ── Compose state ──────────────────────────────────────────────────────────
  const [title, setTitle]               = useState('');
  const [body, setBody]                 = useState('');
  const [recipientType, setRecipientType] = useState('all_customers');
  const [recipientId, setRecipientId]   = useState('');
  const [recipients, setRecipients]     = useState([]);
  const [sending, setSending]           = useState(false);
  const [sendResult, setSendResult]     = useState(null);

  // ── Alert prefs ────────────────────────────────────────────────────────────
  const [prefs, setPrefs]               = useState({ alert_30d: true, alert_15d: true, alert_7d: true });
  const [savingPrefs, setSavingPrefs]   = useState(false);

  // ── Logs ──────────────────────────────────────────────────────────────────
  const [logs, setLogs]                 = useState([]);
  const [logsTotal, setLogsTotal]       = useState(0);
  const [logsPage, setLogsPage]         = useState(1);
  const [loadingLogs, setLoadingLogs]   = useState(false);
  const [detailLog, setDetailLog]       = useState(null);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Check if FCM is configured by SA
    api.get('/notifications/fcm-config')
      .then(r => setFcmConfigured(!!(r.data?.api_key)))
      .catch(() => setFcmConfigured(false));

    // Load alert prefs
    api.get('/notifications/settings')
      .then(r => setPrefs(p => ({ ...p, ...r.data })))
      .catch(() => {});
  }, []);

  // Load specific recipient list when type changes
  useEffect(() => {
    if (!['dealer', 'customer'].includes(recipientType)) {
      setRecipients([]); setRecipientId(''); return;
    }
    const endpoint = recipientType === 'dealer' ? '/admin/dealers?limit=500' : '/admin/customers?limit=500';
    api.get(endpoint)
      .then(r => setRecipients(Array.isArray(r.data) ? r.data : (r.data.items || [])))
      .catch(() => setRecipients([]));
  }, [recipientType]);

  // Load logs
  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const { data } = await api.get(`/notifications/logs?page=${logsPage}&limit=15`);
      setLogs(data.items || []);
      setLogsTotal(data.total || 0);
    } catch {} finally { setLoadingLogs(false); }
  }, [logsPage]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  // ── Send push notification ─────────────────────────────────────────────────
  const handleSend = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!body.trim())  { toast.error("Message body is required"); return; }
    if (['dealer', 'customer'].includes(recipientType) && !recipientId) {
      toast.error("Please select a recipient"); return;
    }
    setSending(true); setSendResult(null);
    try {
      const payload = { title: title.trim(), body: body.trim(), recipient_type: recipientType };
      if (['dealer', 'customer'].includes(recipientType)) payload.recipient_id = recipientId;

      const { data } = await api.post('/notifications/send-push', payload);
      setSendResult({ ok: true, ...data });
      toast.success(data.message || "Push notification sent!");
      setTitle(''); setBody('');
      loadLogs();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Send failed";
      setSendResult({ ok: false, message: msg });
      toast.error(msg);
    } finally { setSending(false); }
  };

  // ── Save alert prefs ───────────────────────────────────────────────────────
  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      await api.put('/notifications/settings', prefs);
      toast.success("Alert preferences saved");
    } catch { toast.error("Failed to save"); }
    finally { setSavingPrefs(false); }
  };

  const viewLogDetail = async (log) => {
    try {
      const { data } = await api.get(`/notifications/logs/${log.id}`);
      setDetailLog(data);
    } catch { toast.error("Failed to load details"); }
  };

  // ── Permission request ─────────────────────────────────────────────────────
  const handleRequestPermission = async () => {
    const result = await requestFCM();
    if (result?.ok) toast.success("Push notifications enabled for your browser!");
    else if (result?.reason) toast.error(result.reason);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="font-heading font-bold text-2xl text-brand-nav">Notifications</h2>
        <p className="text-slate-500 mt-1">Send browser push notifications to your customers and dealers.</p>
      </div>

      {/* FCM not configured banner */}
      {fcmConfigured === false && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800 text-sm">Firebase Push Not Configured</p>
            <p className="text-amber-700 text-xs mt-0.5">
              Ask your SuperAdmin to add a Firebase Cloud Messaging (FCM) provider in the Notification Providers settings.
              Once configured, push notifications will work instantly.
            </p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* ── LEFT: Compose ─────────────────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">

            {/* Channel header */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <Bell className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-brand-nav">Send Push Notification</h3>
                <p className="text-xs text-slate-400">Delivered as browser notifications to users' devices</p>
              </div>
            </div>

            {/* Recipient type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Send To</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {RECIPIENT_TYPES.map(rt => {
                  const Icon = rt.icon;
                  return (
                    <button key={rt.value} onClick={() => setRecipientType(rt.value)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-xs font-semibold transition-all ${
                        recipientType === rt.value
                          ? "border-brand-primary bg-brand-primary/5 text-brand-primary"
                          : "border-slate-200 text-slate-500 hover:border-slate-300"
                      }`}>
                      <Icon className="w-4 h-4" />
                      {rt.label}
                      <span className={`text-[10px] font-normal ${recipientType === rt.value ? "text-brand-primary/70" : "text-slate-400"}`}>
                        {rt.desc}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Specific recipient — searchable dropdown */}
            {['dealer', 'customer'].includes(recipientType) && (() => {
              const label = recipientType === 'dealer' ? 'Dealer' : 'Customer';
              const getName = r => r.user?.name || r.business_name || r.name || 'Unknown';
              const getMobile = r => r.user?.mobile || r.mobile || '';
              const getId = r => r.user_id || r.id;

              const options = recipients.map(r => ({
                value: getId(r),
                label: `${getName(r)} (${getMobile(r)})`,
                recipient: r,
              }));
              
              const selectedOption = options.find(o => o.value === recipientId) || null;

              return (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Select {label}
                  </label>

                  <Select
                    options={options}
                    value={selectedOption}
                    onChange={(selected) => setRecipientId(selected ? selected.value : '')}
                    placeholder={`Search ${label.toLowerCase()} by name or mobile...`}
                    isClearable
                    className="text-sm"
                    classNames={{
                      control: (state) => `!min-h-[44px] !rounded-lg !border-slate-200 focus-within:!border-brand-primary focus-within:!ring-2 focus-within:!ring-brand-primary/30 !shadow-none hover:!border-slate-300`,
                      option: (state) => `!cursor-pointer ${state.isSelected ? '!bg-brand-primary !text-white' : state.isFocused ? '!bg-brand-primary/10 !text-brand-primary' : ''}`,
                      menu: () => `!rounded-xl !border !border-slate-200 !shadow-lg !overflow-hidden`,
                      menuList: () => `!p-0`,
                    }}
                  />
                </div>
              );
            })()}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Notification Title</label>
              <input value={title} onChange={e => setTitle(e.target.value)} maxLength={100}
                placeholder="e.g. Your plan expires in 7 days"
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
              <p className="text-xs text-slate-400 mt-1">{title.length}/100 chars</p>
            </div>

            {/* Body */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
              <textarea rows={4} value={body} onChange={e => setBody(e.target.value)} maxLength={500}
                placeholder="e.g. Renew your DishTV plan before it expires to avoid interruption. Contact us for assistance."
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
              <p className="text-xs text-slate-400 mt-1">{body.length}/500 chars</p>
            </div>

            {/* Preview */}
            {(title || body) && (
              <div className="bg-slate-800 rounded-xl p-4 text-white">
                <div className="text-[10px] text-slate-400 mb-2 uppercase tracking-wider">Preview</div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center shrink-0">
                    <Bell className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{title || "Notification Title"}</div>
                    <div className="text-xs text-slate-300 mt-0.5 line-clamp-2">{body || "Message body..."}</div>
                    <div className="text-[10px] text-slate-500 mt-1">DishLCO · now</div>
                  </div>
                </div>
              </div>
            )}

            {/* Send button */}
            <button onClick={handleSend}
              disabled={sending || !title.trim() || !body.trim() || fcmConfigured === false}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white bg-brand-primary hover:bg-brand-primaryHover transition-all disabled:opacity-50 shadow-sm">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending Push..." : "Send Push Notification"}
            </button>

            {/* Result */}
            {sendResult && (
              <div className={`flex items-start gap-3 p-4 rounded-xl border ${sendResult.ok ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                {sendResult.ok
                  ? <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  : <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />}
                <div>
                  <p className={`text-sm font-semibold ${sendResult.ok ? "text-green-700" : "text-red-700"}`}>{sendResult.message}</p>
                  {sendResult.ok && (
                    <p className="text-xs text-green-600 mt-0.5">
                      {sendResult.sent}/{sendResult.total_tokens} devices · {sendResult.total_users} users targeted
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Status + Prefs ─────────────────────────────────────────── */}
        <div className="space-y-5">

          {/* FCM status card */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-heading font-bold text-base text-brand-nav mb-3">Push Status</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Firebase Configured</span>
                {fcmConfigured === null
                  ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  : fcmConfigured
                    ? <span className="text-green-600 font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Yes</span>
                    : <span className="text-red-500 font-semibold flex items-center gap-1"><XCircle className="w-4 h-4" /> No</span>
                }
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Your browser</span>
                <span className={`font-semibold flex items-center gap-1 ${fcmGranted ? "text-green-600" : "text-slate-400"}`}>
                  {fcmGranted ? <><Bell className="w-4 h-4" /> Subscribed</> : <><BellOff className="w-4 h-4" /> Not subscribed</>}
                </span>
              </div>
            </div>
            {!fcmGranted && fcmConfigured && (
              <button onClick={handleRequestPermission} disabled={fcmLoading}
                className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold border border-brand-primary text-brand-primary hover:bg-brand-primary/5 transition-colors disabled:opacity-50">
                {fcmLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
                Enable for this browser
              </button>
            )}
            <div className="mt-3 bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
              <Smartphone className="w-3.5 h-3.5 inline mr-1" />
              Users receive notifications in their browser when they've allowed permissions on your portal.
            </div>
          </div>

          {/* Alert prefs */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-heading font-bold text-base text-brand-nav mb-1">Auto Expiry Alerts</h3>
            <p className="text-xs text-slate-500 mb-4">Auto push to customers before plan expires.</p>
            <div className="space-y-3">
              {[
                { key: 'alert_30d', label: '30-day warning' },
                { key: 'alert_15d', label: '15-day warning' },
                { key: 'alert_7d',  label: '7-day (urgent)' },
              ].map(a => (
                <label key={a.key} className="flex items-center gap-3 cursor-pointer group">
                  <div className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${prefs[a.key] ? "bg-brand-primary" : "bg-slate-200"}`}
                    onClick={() => setPrefs(p => ({ ...p, [a.key]: !p[a.key] }))}>
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${prefs[a.key] ? "left-5" : "left-0.5"}`} />
                  </div>
                  <span className="text-sm text-slate-700">{a.label}</span>
                </label>
              ))}
            </div>
            <button onClick={savePrefs} disabled={savingPrefs}
              className="mt-4 w-full py-2 rounded-lg text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
              {savingPrefs && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save Preferences
            </button>
          </div>
        </div>
      </div>

      {/* ── Sent History ──────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg text-brand-nav">Sent History</h3>
          <button onClick={loadLogs} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-x-auto">
          {loadingLogs ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No notifications sent yet</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-6 py-3">Date & Time</th>
                  <th className="text-left px-6 py-3">Title</th>
                  <th className="text-left px-6 py-3">To</th>
                  <th className="text-left px-6 py-3">Status</th>
                  <th className="text-right px-6 py-3">Devices</th>
                  <th className="text-right px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const sb = STATUS_BADGE[log.status] || STATUS_BADGE.sent;
                  const SIcon = sb.icon;
                  return (
                    <tr key={log.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-6 py-3 text-xs text-slate-500 font-mono whitespace-nowrap">
                        {(log.created_at || '').slice(0, 16).replace('T', ' ')}
                      </td>
                      <td className="px-6 py-3">
                        <div className="font-medium text-slate-800 text-xs">{log.subject || log.message?.slice(0, 40)}</div>
                        <div className="text-xs text-slate-400 truncate max-w-[200px]">{log.message?.slice(0, 60)}</div>
                      </td>
                      <td className="px-6 py-3 text-xs text-slate-500 capitalize">{(log.recipient_type || '').replace(/_/g, ' ')}</td>
                      <td className="px-6 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${sb.cls}`}>
                          <SIcon className="w-3 h-3" /> {log.status}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-right text-xs font-mono">
                        <span className="text-green-600">{log.sent_count ?? 0}</span>
                        {(log.failed_count ?? 0) > 0 && <span className="text-red-500">/{log.failed_count}</span>}
                        <span className="text-slate-400">/{log.recipients_count ?? 0}</span>
                      </td>
                      <td className="px-6 py-3 text-right">
                        <button onClick={() => viewLogDetail(log)} className="text-xs text-brand-primary hover:underline inline-flex items-center gap-1">
                          <Eye className="w-3 h-3" /> Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <Pagination page={logsPage} pageCount={Math.ceil(logsTotal / 15)} onChange={setLogsPage} total={logsTotal} />
      </div>

      {/* ── Detail modal ────────────────────────────────────────────────────── */}
      {detailLog && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setDetailLog(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-heading font-bold text-xl text-brand-nav">Notification Detail</h4>
              <button onClick={() => setDetailLog(null)} className="text-slate-400 hover:text-slate-600 text-lg">✕</button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ['Title', detailLog.subject],
                  ['Status', detailLog.status],
                  ['Sent to', (detailLog.recipient_type || '').replace(/_/g, ' ')],
                  ['Devices', `${detailLog.sent_count ?? 0} sent / ${detailLog.failed_count ?? 0} failed`],
                  ['Sent By', detailLog.sent_by_name],
                  ['Date', (detailLog.created_at || '').slice(0, 19).replace('T', ' ')],
                ].map(([k, v]) => (
                  <div key={k} className="bg-slate-50 rounded-lg px-3 py-2">
                    <div className="text-slate-400 mb-0.5">{k}</div>
                    <div className="font-semibold text-slate-700 capitalize">{v ?? '—'}</div>
                  </div>
                ))}
              </div>
              <div className="bg-slate-50 rounded-lg px-3 py-2 text-xs">
                <div className="text-slate-400 mb-1">Message</div>
                <pre className="whitespace-pre-wrap font-body text-slate-700">{detailLog.message}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
